import {
  GuidedInterviewPrepMode,
  GuidedInterviewPrepType,
} from "../services/jobApplicationService";

export interface GuidedInterviewTopicOption {
  value: string;
  label: string;
  description: string;
  defaultQuestionCount?: number;
  source?: "library" | "job_description" | "custom";
}

export interface GuidedInterviewTopicContext {
  technologies?: string[];
  positionSpecialization?: string[];
  responsibilities?: string[];
  requiredQualifications?: string[];
  optionalQualifications?: string[];
  additionalQualifications?: string[];
}

export const PREP_TYPE_OPTIONS: Array<{
  value: GuidedInterviewPrepType;
  label: string;
  description: string;
}> = [
  {
    value: "recruiter_hr",
    label: "Recruiter / HR",
    description: "Motivation, fit, logistics, communication style, and company alignment.",
  },
  {
    value: "behavioral",
    label: "Behavioral",
    description: "Leadership, conflict, ownership, ambiguity, and measurable impact stories.",
  },
  {
    value: "technical_functional",
    label: "Technical / Functional",
    description: "Role-specific depth, domain judgment, problem solving, and execution rigor.",
  },
  {
    value: "hiring_manager",
    label: "Hiring Manager",
    description: "Role fit, priorities, tradeoffs, collaboration, and business impact.",
  },
  {
    value: "system_design_case_study",
    label: "System Design / Case Study",
    description: "Architecture, frameworks, structured reasoning, tradeoffs, and constraints.",
  },
  {
    value: "panel_final_round",
    label: "Panel / Final Round",
    description: "Consistency under pressure, stakeholder alignment, and executive-level synthesis.",
  },
  {
    value: "presentation_demo",
    label: "Presentation / Demo",
    description: "Storytelling, clarity, Q&A handling, and role-relevant demonstrations.",
  },
  {
    value: "executive",
    label: "Executive",
    description: "Strategy, influence, operating judgment, and leadership credibility.",
  },
];

export const PREP_MODE_OPTIONS: Array<{
  value: GuidedInterviewPrepMode;
  label: string;
  description: string;
}> = [
  {
    value: "model_answers",
    label: "Model Answers",
    description: "Study the hardest likely questions with concise and extended answer examples.",
  },
  {
    value: "answers_coaching",
    label: "Answers + Coaching",
    description: "Get model answers plus analysis of what works, what sounds weak, and how to recover.",
  },
];

const SUGGESTED_INTERVIEW_TOPICS: Record<GuidedInterviewPrepType, GuidedInterviewTopicOption[]> = {
  recruiter_hr: [
    {
      value: "motivation_for_role",
      label: "Motivation for the Role",
      description: "Why this role, why this company, and why now.",
      defaultQuestionCount: 3,
    },
    {
      value: "company_fit",
      label: "Company Fit",
      description: "Values alignment, culture, and the story you want to tell.",
      defaultQuestionCount: 3,
    },
    {
      value: "career_narrative",
      label: "Career Narrative",
      description: "How your path connects cleanly to this opportunity.",
      defaultQuestionCount: 3,
    },
    {
      value: "compensation_and_logistics",
      label: "Compensation and Logistics",
      description: "Availability, flexibility, and compensation framing.",
      defaultQuestionCount: 2,
    },
  ],
  behavioral: [
    {
      value: "leadership_stories",
      label: "Leadership Stories",
      description: "Ownership, influence, and measurable impact.",
      defaultQuestionCount: 3,
    },
    {
      value: "conflict_handling",
      label: "Conflict Handling",
      description: "Disagreement, pushback, and collaboration under pressure.",
      defaultQuestionCount: 3,
    },
    {
      value: "ambiguity_and_scope",
      label: "Ambiguity and Scope",
      description: "How you make progress when the path is not fully defined.",
      defaultQuestionCount: 3,
    },
    {
      value: "failure_and_recovery",
      label: "Failure and Recovery",
      description: "Accountability, learning, and what changed afterward.",
      defaultQuestionCount: 2,
    },
  ],
  technical_functional: [
    {
      value: "role_depth",
      label: "Role Depth",
      description: "Domain knowledge and day-to-day execution depth.",
      defaultQuestionCount: 3,
    },
    {
      value: "problem_solving",
      label: "Problem Solving",
      description: "How you break down unknowns and make decisions.",
      defaultQuestionCount: 3,
    },
    {
      value: "quality_and_reliability",
      label: "Quality and Reliability",
      description: "Debugging, testing, and production readiness.",
      defaultQuestionCount: 3,
    },
    {
      value: "tools_and_workflows",
      label: "Tools and Workflows",
      description: "The operating habits that make you effective.",
      defaultQuestionCount: 2,
    },
  ],
  hiring_manager: [
    {
      value: "first_90_days",
      label: "First 90 Days",
      description: "How you will prioritize, execute, and build trust.",
      defaultQuestionCount: 3,
    },
    {
      value: "business_impact",
      label: "Business Impact",
      description: "How your work ties to team and company outcomes.",
      defaultQuestionCount: 3,
    },
    {
      value: "collaboration",
      label: "Collaboration",
      description: "Stakeholder alignment and team operating style.",
      defaultQuestionCount: 3,
    },
    {
      value: "decision_making",
      label: "Decision Making",
      description: "Tradeoffs, judgment, and ownership scope.",
      defaultQuestionCount: 2,
    },
  ],
  system_design_case_study: [
    {
      value: "scale_and_performance",
      label: "Scale and Performance",
      description: "Capacity, bottlenecks, and reliability at growth.",
      defaultQuestionCount: 3,
    },
    {
      value: "security_and_compliance",
      label: "Security and Compliance",
      description: "Boundaries, access, and regulatory constraints.",
      defaultQuestionCount: 3,
    },
    {
      value: "tradeoffs_and_cost",
      label: "Tradeoffs and Cost",
      description: "What you optimize for and what you intentionally trade off.",
      defaultQuestionCount: 3,
    },
    {
      value: "observability_and_failure_modes",
      label: "Observability and Failure Modes",
      description: "How you detect issues and recover cleanly.",
      defaultQuestionCount: 2,
    },
  ],
  panel_final_round: [
    {
      value: "consistent_narrative",
      label: "Consistent Narrative",
      description: "A through-line across multiple interviewers.",
      defaultQuestionCount: 3,
    },
    {
      value: "executive_presence",
      label: "Executive Presence",
      description: "Confidence, structure, and concise answers.",
      defaultQuestionCount: 3,
    },
    {
      value: "pressure_testing",
      label: "Pressure Testing",
      description: "How you handle pushback and hard follow-ups.",
      defaultQuestionCount: 3,
    },
    {
      value: "closing_strongly",
      label: "Closing Strongly",
      description: "Questions, summary, and final impressions.",
      defaultQuestionCount: 2,
    },
  ],
  presentation_demo: [
    {
      value: "story_structure",
      label: "Story Structure",
      description: "A clean arc from problem to recommendation.",
      defaultQuestionCount: 3,
    },
    {
      value: "demo_flow",
      label: "Demo Flow",
      description: "How to sequence the walkthrough and avoid dead ends.",
      defaultQuestionCount: 3,
    },
    {
      value: "question_handling",
      label: "Question Handling",
      description: "How to respond without losing the room.",
      defaultQuestionCount: 3,
    },
    {
      value: "time_management",
      label: "Time Management",
      description: "Pacing, prioritization, and polished delivery.",
      defaultQuestionCount: 2,
    },
  ],
  executive: [
    {
      value: "strategic_judgment",
      label: "Strategic Judgment",
      description: "How you define the right problem and the right leverage point.",
      defaultQuestionCount: 3,
    },
    {
      value: "organizational_influence",
      label: "Organizational Influence",
      description: "Aligning teams, leaders, and competing priorities.",
      defaultQuestionCount: 3,
    },
    {
      value: "metrics_and_accountability",
      label: "Metrics and Accountability",
      description: "How you use numbers to manage and communicate.",
      defaultQuestionCount: 3,
    },
    {
      value: "change_leadership",
      label: "Change Leadership",
      description: "Leading through uncertainty and transformation.",
      defaultQuestionCount: 2,
    },
  ],
};

const JD_TOPIC_HINTS: Array<{
  value: string;
  label: string;
  description: string;
  defaultQuestionCount: number;
  patterns: RegExp[];
}> = [
  {
    value: "system_architecture",
    label: "System Architecture",
    description: "Architecture tradeoffs, scalability, and technical judgment pulled from the job description.",
    defaultQuestionCount: 4,
    patterns: [/system design/i, /architecture/i, /distributed/i, /scalab/i, /platform/i],
  },
  {
    value: "incident_response",
    label: "Incident Response",
    description: "Operational readiness, debugging, failure analysis, and recovery patterns.",
    defaultQuestionCount: 4,
    patterns: [/incident/i, /on-call/i, /production/i, /reliab/i, /observab/i, /debug/i],
  },
  {
    value: "security_compliance",
    label: "Security and Compliance",
    description: "Security boundaries, risk controls, privacy, and regulated-environment rigor.",
    defaultQuestionCount: 4,
    patterns: [/security/i, /privacy/i, /compliance/i, /soc ?2/i, /hipaa/i, /gdpr/i, /risk/i],
  },
  {
    value: "stakeholder_alignment",
    label: "Stakeholder Alignment",
    description: "Cross-functional influence, alignment, and working through disagreement.",
    defaultQuestionCount: 4,
    patterns: [/stakeholder/i, /cross-functional/i, /partner/i, /executive/i, /collaborat/i],
  },
  {
    value: "product_strategy",
    label: "Product Strategy",
    description: "Prioritization, roadmap judgment, and product sense grounded in the role description.",
    defaultQuestionCount: 4,
    patterns: [/roadmap/i, /priorit/i, /product strategy/i, /product sense/i, /go-to-market/i],
  },
  {
    value: "data_and_metrics",
    label: "Data and Metrics",
    description: "Decision-making with metrics, experimentation, and analytical rigor.",
    defaultQuestionCount: 4,
    patterns: [/metric/i, /analytics/i, /experimen/i, /ab test/i, /sql/i, /data/i, /kpi/i],
  },
  {
    value: "ai_ml_depth",
    label: "AI / ML Depth",
    description: "Model judgment, evaluation, prompt quality, and AI system tradeoffs.",
    defaultQuestionCount: 4,
    patterns: [/machine learning/i, /\bml\b/i, /artificial intelligence/i, /\bai\b/i, /llm/i, /prompt/i],
  },
  {
    value: "customer_communication",
    label: "Customer Communication",
    description: "Customer empathy, demos, discovery, and communication under pressure.",
    defaultQuestionCount: 3,
    patterns: [/customer/i, /client/i, /presentation/i, /demo/i, /discovery/i, /communication/i],
  },
  {
    value: "leadership_management",
    label: "Leadership and Management",
    description: "Team leadership, coaching, hiring, and organizational influence.",
    defaultQuestionCount: 4,
    patterns: [/leadership/i, /manage/i, /mentor/i, /hire/i, /team building/i],
  },
];

const EXACT_TECH_TOPIC_HINTS: Array<{
  label: string;
  patterns: RegExp[];
}> = [
  {
    label: "AWS EC2",
    patterns: [/\baws ec2\b/i, /\bec2\b/i],
  },
  {
    label: "AWS Lambda",
    patterns: [/\baws lambda\b/i, /\blambda\b/i],
  },
  {
    label: "Snowflake",
    patterns: [/\bsnowflake\b/i],
  },
  {
    label: "Streamlit",
    patterns: [/\bstreamlit\b/i],
  },
  {
    label: "Python",
    patterns: [/\bpython\b/i],
  },
  {
    label: "TypeScript",
    patterns: [/\btypescript\b/i],
  },
  {
    label: "JavaScript",
    patterns: [/\bjavascript\b/i],
  },
  {
    label: "React",
    patterns: [/\breact\b/i],
  },
  {
    label: "Node.js",
    patterns: [/\bnode\.?js\b/i, /\bnode\b/i],
  },
  {
    label: "Kubernetes",
    patterns: [/\bkubernetes\b/i, /\bk8s\b/i],
  },
  {
    label: "Docker",
    patterns: [/\bdocker\b/i],
  },
  {
    label: "CloudFormation",
    patterns: [/\bcloudformation\b/i],
  },
];

const inferExactTechnologyTopics = (
  jobDescription?: string | null,
  position?: string | null,
  context?: GuidedInterviewTopicContext,
): GuidedInterviewTopicOption[] => {
  const exactCandidates = [
    ...(context?.technologies || []),
    ...(context?.positionSpecialization || []),
    ...(context?.responsibilities || []),
    ...(context?.requiredQualifications || []),
    ...(context?.optionalQualifications || []),
    ...(context?.additionalQualifications || []),
    position || "",
    jobDescription || "",
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join("\n");

  if (!exactCandidates) {
    return [];
  }

  return EXACT_TECH_TOPIC_HINTS.filter((hint) =>
    hint.patterns.some((pattern) => pattern.test(exactCandidates)),
  ).map((hint) => ({
    value: hint.label,
    label: hint.label,
    description: `Explicitly named in this application's stack or job description: ${hint.label}.`,
    defaultQuestionCount: 4,
    source: "job_description" as const,
  }));
};

const inferTopicsFromJobDescription = (
  jobDescription?: string | null,
  position?: string | null,
  context?: GuidedInterviewTopicContext,
): GuidedInterviewTopicOption[] => {
  const jdText = [
    position || "",
    jobDescription || "",
    ...(context?.responsibilities || []),
    ...(context?.requiredQualifications || []),
    ...(context?.optionalQualifications || []),
    ...(context?.additionalQualifications || []),
  ]
    .filter(Boolean)
    .join("\n")
    .trim();
  if (!jdText) {
    return [];
  }

  return JD_TOPIC_HINTS.filter((hint) =>
    hint.patterns.some((pattern) => pattern.test(jdText)),
  ).map((hint) => ({
    value: hint.value,
    label: hint.label,
    description: hint.description,
    defaultQuestionCount: hint.defaultQuestionCount,
    source: "job_description",
  }));
};

export const getSuggestedTopics = (
  prepType: GuidedInterviewPrepType,
  jobDescription?: string | null,
  position?: string | null,
  context?: GuidedInterviewTopicContext,
): GuidedInterviewTopicOption[] => {
  const exactTechTopics = inferExactTechnologyTopics(jobDescription, position, context);
  const inferredTopics = inferTopicsFromJobDescription(jobDescription, position, context);
  const suggestedTopics = (SUGGESTED_INTERVIEW_TOPICS[prepType] || []).map((topic) => ({
    ...topic,
    source: "library" as const,
  }));
  const seenValues = new Set<string>();
  const mergedTopics: GuidedInterviewTopicOption[] = [];

  [...exactTechTopics, ...inferredTopics, ...suggestedTopics].forEach((topic) => {
    if (seenValues.has(topic.value)) {
      return;
    }
    seenValues.add(topic.value);
    mergedTopics.push(topic);
  });

  return mergedTopics;
};

export const getPrepTypeLabel = (value?: string | null) =>
  PREP_TYPE_OPTIONS.find((option) => option.value === value)?.label || value || "Interview Prep";

export const getOutputModeLabel = (value?: string | null) =>
  PREP_MODE_OPTIONS.find((option) => option.value === value)?.label || value || "Model Answers";

export const getSuggestedFocusAreas = (
  prepType: GuidedInterviewPrepType,
  jobDescription?: string | null,
  position?: string | null,
  context?: GuidedInterviewTopicContext,
): string[] =>
  getSuggestedTopics(prepType, jobDescription, position, context).map((topic) => topic.label);
